import React, { useState, useEffect } from 'react';
import { 
    Box, Typography, Paper, Button, TextField, 
    InputAdornment, Chip, IconButton, Table, TableBody, 
    TableCell, TableContainer, TableHead, TableRow, FormControl, 
    InputLabel, Select, MenuItem, Switch, FormControlLabel,
    CircularProgress, Alert, TablePagination, Menu, Tooltip
} from '@mui/material';
import { useSelector } from 'react-redux';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import RatePlanFormModal from '../../components/RatePlanFormModal';
import DeleteConfirmationModal from '../../components/DeleteConfirmationModal';
import ExportFormatModal from '../../components/ExportFormatModal';
import { convertCsvToExportFormat } from '../../utils/exportUtils';
import { RateService } from '../../api/services/rateService';
import { RoomService } from '../../api/services/roomService';
import ReplayIcon from '@mui/icons-material/Replay';
import { useCurrency, formatCurrency } from '../../utils/currency';
import { extractTotalCount, standardFilterContainerSx, standardSearchInputSx, standardFilterSelectSx, standardFilterInputSx, standardResetButtonSx, standardRefreshButtonSx, standardSecondaryButtonSx } from '../../components/common/tableStyles';

export default function RatePlansTab({ setNotification }) {
    const { currency } = useCurrency();
    const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
    const tenantId = authData.tenantId || '1';
    const { tenants } = useSelector((state) => state.auth || { tenants: [] });
    const tenant = tenants.find(t => String(t.tenantId) === String(tenantId) || String(t.id) === String(tenantId) || String(t._id) === String(tenantId));
    const hotelStatus = tenant?.status || 'Active';
    const isRateLocked = hotelStatus === 'Suspended' || hotelStatus === 'Archived';

    const [searchTerm, setSearchTerm] = useState('');
    const [typeFilter, setTypeFilter] = useState('All');
    const [roomTypeFilter, setRoomTypeFilter] = useState('All');
    const [channelFilter, setChannelFilter] = useState('All');
    const [startDateFilter, setStartDateFilter] = useState('');
    const [endDateFilter, setEndDateFilter] = useState('');
    
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [totalPlans, setTotalPlans] = useState(0);
    const [ratePlans, setRatePlans] = useState([]);
    const [roomTypes, setRoomTypes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isExporting, setIsExporting] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [exportModalOpen, setExportModalOpen] = useState(false);
    
    const [rateModal, setRateModal] = useState({ open: false, data: null });
    const [deleteModal, setDeleteModal] = useState({ open: false, plan: null });

    useEffect(() => {
        fetchRoomTypes();
    }, []);

    useEffect(() => {
        fetchRatePlans();
    }, [page, rowsPerPage, searchTerm, typeFilter, roomTypeFilter, channelFilter, startDateFilter, endDateFilter]);

    const fetchRoomTypes = async () => {
        try {
            const response = await RoomService.getRoomTypes();
            setRoomTypes(Array.isArray(response) ? response : response?.data || []);
        } catch (err) {
            console.error('Failed to fetch room types:', err);
        }
    };

    const fetchRatePlans = async () => {
        setLoading(true);
        setError(null);
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId;
            const params = {
                page: page,
                limit: rowsPerPage,
                ...(searchTerm && { search: searchTerm }),
                ...(typeFilter !== 'All' && { type: typeFilter }),
                ...(roomTypeFilter !== 'All' && { roomTypeId: roomTypeFilter }),
                ...(channelFilter !== 'All' && { channel: channelFilter }),
                ...(startDateFilter && { startDate: `${startDateFilter}T00:00:00.000Z` }),
                ...(endDateFilter && { endDate: `${endDateFilter}T23:59:59.999Z` })
            };
            const response = await RateService.getRatePlans(tenantId, params);
            const plansArray = Array.isArray(response.data) ? response.data : (Array.isArray(response) ? response : []);
            setRatePlans(plansArray);
            setTotalPlans(extractTotalCount(response, plansArray));
        } catch (err) {
            console.error('Failed to fetch rate plans:', err);
            setError('Failed to load rate plans. Please try again later.');
            setNotification({ open: true, message: 'Failed to load rate plans.', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleExport = async (format) => {
        setExportModalOpen(false);
        setIsExporting(true);
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';
            const response = await RateService.exportRatePlans(tenantId, 'csv');
            
            let csvText = '';
            if (response.data instanceof Blob) {
                csvText = await response.data.text();
            } else if (typeof response.data === 'string') {
                csvText = response.data;
            } else {
                csvText = JSON.stringify(response.data);
            }
            
            const filenameBase = `rate-plans-export-${new Date().toISOString().split('T')[0]}`;
            await convertCsvToExportFormat(csvText, format, filenameBase);
            
            setNotification({ open: true, message: `Rate plans exported successfully!`, severity: 'success' });
        } catch (err) {
            console.error('Failed to export rate plans:', err);
            setNotification({ open: true, message: 'Failed to export rate plans. Please try again.', severity: 'error' });
        } finally {
            setIsExporting(false);
        }
    };

    const handleSavePlan = () => {
        setNotification({ open: true, message: 'Rate plan saved successfully!', severity: 'success' });
        fetchRatePlans();
    };

    const handleConfirmDelete = async () => {
        if (!deleteModal.plan) return;

        setIsDeleting(true);
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';
            await RateService.deleteRatePlan(deleteModal.plan.id, tenantId);
            setRatePlans(prev => prev.filter(p => p.id !== deleteModal.plan.id));
            setNotification({ open: true, message: 'Rate plan deleted successfully!', severity: 'success' });
            setDeleteModal({ open: false, plan: null });
            await fetchRatePlans();
        } catch (err) {
            console.error('Failed to delete rate plan:', err);
            setNotification({ open: true, message: 'Failed to delete rate plan. Please try again.', severity: 'error' });
        } finally {
            setIsDeleting(false);
        }
    };

    const handleToggleStatus = async (plan) => {
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';
            const newStatus = plan.status === 'Active' ? 'Inactive' : 'Active';
            await RateService.updateRatePlan(plan.id, { status: newStatus }, tenantId);
            setRatePlans(prev => prev.map(p => p.id === plan.id ? { ...p, status: newStatus } : p));
            setNotification({ open: true, message: `Rate plan ${newStatus.toLowerCase()} successfully!`, severity: 'success' });
            await fetchRatePlans();
        } catch (err) {
            console.error('Failed to toggle rate plan status:', err);
            setNotification({ open: true, message: 'Failed to update status.', severity: 'error' });
        }
    };

    const getRateTypeColor = (type) => {
        switch (type) {
            case 'Seasonal': return 'success';
            case 'Corporate': return 'warning';
            case 'Promotional': return 'error';
            default: return 'default';
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return dateString.split('T')[0];
    };

    // Server-side pagination and filtering is now used.

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2, gap: 1.5 }}>
                <Button 
                    variant="outlined" 
                    startIcon={isExporting ? <CircularProgress size={18} color="inherit" /> : <FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />}
                    onClick={() => setExportModalOpen(true)}
                    disabled={isExporting}
                    sx={standardSecondaryButtonSx}
                >
                    Export
                </Button>
                <ExportFormatModal
                    open={exportModalOpen}
                    onClose={() => setExportModalOpen(false)}
                    onExport={handleExport}
                    isExporting={isExporting}
                    title="Export Rate Plans"
                />
                <Tooltip title={isRateLocked ? "Rate modifications are locked while property is Suspended/Archived" : ""}>
                    <span>
                        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setRateModal({ open: true, data: null })} disabled={isRateLocked}>
                            Create Rate Plan
                        </Button>
                    </span>
                </Tooltip>
            </Box>

            <Box sx={standardFilterContainerSx}>
                <TextField
                    size="small"
                    placeholder="Search plan name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={standardSearchInputSx}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#98a0aa', fontSize: 18 }} /></InputAdornment>,
                    }}
                />
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                        <Select value={typeFilter} displayEmpty onChange={(e) => setTypeFilter(e.target.value)} sx={standardFilterSelectSx}>
                            <MenuItem value="All">All Types</MenuItem>
                            <MenuItem value="Base">Base</MenuItem>
                            <MenuItem value="Seasonal">Seasonal</MenuItem>
                            <MenuItem value="Corporate">Corporate</MenuItem>
                            <MenuItem value="Promotional">Promotional</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <Select value={roomTypeFilter} displayEmpty onChange={(e) => setRoomTypeFilter(e.target.value)} sx={standardFilterSelectSx}>
                            <MenuItem value="All">All Rooms</MenuItem>
                            {roomTypes.map(t => (
                                <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <Select value={channelFilter} displayEmpty onChange={(e) => setChannelFilter(e.target.value)} sx={standardFilterSelectSx}>
                            <MenuItem value="All">All Channels</MenuItem>
                            <MenuItem value="Direct">Direct</MenuItem>
                            <MenuItem value="OTA">OTA</MenuItem>
                            <MenuItem value="Corporate">Corporate</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField
                        size="small"
                        type="date"
                        value={startDateFilter}
                        onChange={(e) => setStartDateFilter(e.target.value)}
                        sx={{ width: 140, ...standardFilterInputSx }}
                    />
                    <TextField
                        size="small"
                        type="date"
                        value={endDateFilter}
                        onChange={(e) => setEndDateFilter(e.target.value)}
                        sx={{ width: 140, ...standardFilterInputSx }}
                    />
                    {(searchTerm !== '' || typeFilter !== 'All' || roomTypeFilter !== 'All' || channelFilter !== 'All' || startDateFilter !== '' || endDateFilter !== '') && (
                        <Button 
                            variant="outlined" 
                            startIcon={<ReplayIcon sx={{ fontSize: 16 }} />} 
                            sx={standardResetButtonSx}
                            onClick={() => {
                                setSearchTerm('');
                                setTypeFilter('All');
                                setRoomTypeFilter('All');
                                setChannelFilter('All');
                                setStartDateFilter('');
                                setEndDateFilter('');
                            }}
                            disabled={loading}
                        >
                            Reset
                        </Button>
                    )}
                    <IconButton onClick={fetchRatePlans} disabled={loading} size="small" sx={standardRefreshButtonSx}>
                        <RefreshIcon fontSize="small" />
                    </IconButton>
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                    {error}
                </Alert>
            )}

            <TableContainer component={Paper} sx={{ position: 'relative', minHeight: 200 }}>
                {loading && (
                    <Box sx={{ 
                        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, 
                        display: 'flex', justifyContent: 'center', alignItems: 'center', 
                        bgcolor: 'rgba(255,255,255,0.7)', zIndex: 1 
                    }}>
                        <CircularProgress size={40} />
                    </Box>
                )}
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Plan Name</TableCell>
                            <TableCell>Room Type</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Channels</TableCell>
                            <TableCell>Rate / Modifier</TableCell>
                            <TableCell>Validity</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {ratePlans.length > 0 ? (
                            ratePlans.map((plan) => (
                                <TableRow key={plan.id} hover>
                                    <TableCell><b>{plan.name}</b></TableCell>
                                    <TableCell>{plan.roomType?.name || 'All Room Types'}</TableCell>
                                    <TableCell>
                                        <Chip label={plan.type} size="small" color={getRateTypeColor(plan.type)} variant="outlined" />
                                    </TableCell>
                                    <TableCell>
                                        {Array.isArray(plan.channels) ? plan.channels.join(', ') : (plan.channels || 'Direct')}
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="bold">{formatCurrency(plan.rate, currency)}</Typography>
                                        {parseFloat(plan.modifier) !== 0 && !isNaN(parseFloat(plan.modifier)) && (
                                            <Typography variant="caption" color="text.secondary">
                                                Modifier: {plan.modifier}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption" display="block">From: {formatDate(plan.validFrom)}</Typography>
                                        <Typography variant="caption" display="block">To: {formatDate(plan.validTo)}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <FormControlLabel
                                            control={
                                                <Switch 
                                                    size="small" 
                                                    checked={plan.status === 'Active'} 
                                                    onChange={() => handleToggleStatus(plan)}
                                                    color="success"
                                                    disabled={isRateLocked}
                                                />
                                            }
                                            label={<Typography variant="body2">{plan.status}</Typography>}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title={isRateLocked ? "Rate modifications locked" : ""}>
                                            <span>
                                                <IconButton size="small" color="primary" disabled={plan.isDefault || isRateLocked} onClick={() => setRateModal({ open: true, data: plan })}><EditIcon fontSize="small" /></IconButton>
                                            </span>
                                        </Tooltip>
                                        <Tooltip title={isRateLocked ? "Rate modifications locked" : ""}>
                                            <span>
                                                <IconButton size="small" color="error" disabled={plan.isDefault || isRateLocked} onClick={() => setDeleteModal({ open: true, plan })}><DeleteIcon fontSize="small" /></IconButton>
                                            </span>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : !loading && (
                            <TableRow>
                                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                    <Typography color="text.secondary">No rate plans found.</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={ratePlans.length === 0 ? 0 : totalPlans}
                    page={page}
                    onPageChange={(e, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                    rowsPerPageOptions={[20, 50, 100]}
                />
            </TableContainer>

            <RatePlanFormModal 
                open={rateModal.open}
                onClose={() => setRateModal({ open: false, data: null })}
                onSave={handleSavePlan}
                editingPlan={rateModal.data}
            />

            <DeleteConfirmationModal 
                open={deleteModal.open}
                onClose={() => setDeleteModal({ open: false, plan: null })}
                onConfirm={handleConfirmDelete}
                title="Delete Rate Plan"
                message={deleteModal.plan ? `Are you sure you want to permanently delete the "${deleteModal.plan.name}" rate plan?` : ''}
                loading={isDeleting}
            />
        </Box>
    );
}
